import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Cancel01Icon,
  ArrowRight01Icon,
  Refresh01Icon,
  CheckmarkCircle01Icon
} from '@hugeicons/core-free-icons';
import ResponsiveModal from './ResponsiveModal';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const BalanceSyncTutorialModal = ({ isOpen, onClose, onDismissed }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDisabling, setIsDisabling] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const totalSlides = 2;

  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
      setDontShowAgain(false);
      // Record that tutorial was shown
      api.post('/balance-sync-tutorial/record-shown').catch(() => {});
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000); // 4 seconds per slide

    return () => clearInterval(timer);
  }, []);

  const handleDismiss = async () => {
    if (dontShowAgain) {
      setIsDisabling(true);
      try {
        await api.post('/balance-sync-tutorial/disable');
        addToast("Tutorial disabled. You can re-enable it in settings.", "success");
        onDismissed?.(true);
      } catch (err) {
        addToast(err.message || "Failed to disable tutorial", "error");
      } finally {
        setIsDisabling(false);
      }
    } else {
      onDismissed?.(false);
    }
    onClose();
  };

  const getImageUrl = (slideIndex) => {
    const role = user?.role || 'client';
    if (slideIndex === 0) {
      // First slide is role-specific
      return `/modals/teach_balance_sync/image1_${role}.png`;
    }
    // Second slide is same for everyone
    return '/modals/teach_balance_sync/image2.png';
  };

  const getSlideContent = (slideIndex) => {
    if (slideIndex === 0) {
      return {
        title: user?.role === 'agent' ? 'Step 1: Open Balance Modal' : 'Step 1: Open Balance Modal',
        description: user?.role === 'agent'
          ? 'Click on your balance display at the top of the screen to open the balance management modal.'
          : 'Tap on your balance shown at the top of the screen to view your account details.'
      };
    }
    return {
      title: 'Step 2: Refresh Your Balance',
      description: 'Click the "Refresh Balance" button to sync your latest Deriv balance with Safi. This ensures your available funds are up to date.'
    };
  };

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleDismiss} showCloseButton={false}>
      <div className="py-6 px-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-soft-green/10 rounded-xl text-soft-green">
              <HugeiconsIcon icon={Refresh01Icon} size={20} />
            </div>
            <h3 className="text-lg font-black text-charcoal dark:text-white">Sync Your Balance</h3>
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors text-muted-gray"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={24} />
          </button>
        </div>

        {/* Carousel */}
        <div className="relative mb-6">
          <div className="overflow-hidden rounded-3xl border-2 border-light-gray/20 dark:border-gray-700 bg-off-white dark:bg-gray-800">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {[0, 1].map((slideIndex) => {
                const content = getSlideContent(slideIndex);
                return (
                  <div key={slideIndex} className="w-full flex-shrink-0">
                    <div className="aspect-[4/3] w-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                      <img
                        src={getImageUrl(slideIndex)}
                        alt={content.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage: ' + getImageUrl(slideIndex) + '%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="text-base font-black text-charcoal dark:text-white mb-2">{content.title}</h4>
                      <p className="text-sm text-muted-gray font-medium leading-relaxed">{content.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {[0, 1].map((slideIndex) => (
              <button
                key={slideIndex}
                onClick={() => setCurrentSlide(slideIndex)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentSlide === slideIndex
                    ? 'w-8 bg-soft-green'
                    : 'w-2 bg-gray-300 dark:bg-gray-700'
                }`}
                aria-label={`Go to slide ${slideIndex + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 px-2">
          <label className="flex items-center gap-3 p-4 bg-off-white dark:bg-gray-800 rounded-2xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-5 h-5 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-soft-green focus:ring-soft-green focus:ring-2"
            />
            <span className="text-sm font-bold text-charcoal dark:text-white">
              Don't show this tutorial again
            </span>
          </label>

          <button
            onClick={handleDismiss}
            disabled={isDisabling && dontShowAgain}
            className="w-full py-4 bg-charcoal hover:bg-charcoal/90 dark:bg-soft-green dark:hover:bg-soft-green/90 text-white dark:text-black rounded-2xl font-black text-sm shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isDisabling && dontShowAgain ? (
              <>
                <HugeiconsIcon icon={Refresh01Icon} className="animate-spin" size={18} />
                Saving...
              </>
            ) : (
              <>
                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} />
                Got it, Dismiss
              </>
            )}
          </button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default BalanceSyncTutorialModal;
